import React, { useCallback, useEffect, useState } from "react";
import { SubHeader } from "../../../utils/componentes-utiles/SubHeader";
import { getUsuarios } from "../../../services/apis";
import { useNavigate } from "react-router-dom";

import {
	CircularProgress,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Pagination,
	Select,
	TextField,
	Typography,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";

import { UsuarioCard } from "./UsuarioCard";
import { FormCrearUsuario } from "./FormCrearUsuario";
import { OpcionesMenuFunc } from "../../../utils/componentes-utiles/OpcionesMenuFunc";

const useDebouncedValue = (value, delay = 300) => {
	const [debounced, setDebounced] = useState(value);

	useEffect(() => {
		const timeoutId = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(timeoutId);
	}, [value, delay]);

	return debounced;
};

export const Usuarios = () => {
	const navigate = useNavigate();

	const [usuarios, setUsuarios] = useState([]);
	const [roles, setRoles] = useState([]);
	const [departamentos, setDepartamentos] = useState([]);
	const [clientes, setClientes] = useState([]);
	const [delegaciones, setDelegaciones] = useState([]);
	const [campanas, setCampanas] = useState([]);
	const [opciones, setOpciones] = useState([]);
	const [opcionesUsuario, setOpcionesUsuario] = useState([]);

	const [loaded, setLoaded] = useState(false);
	const [loading, setLoading] = useState(false);
	const [openCrearUsuario, setOpenCrearUsuario] = useState(false);

	const [filtroRol, setFiltroRol] = useState("");
	const [filtroDepartamento, setFiltroDepartamento] = useState("");
	const [busqueda, setBusqueda] = useState("");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(25);
	const [totalPages, setTotalPages] = useState(1);
	const [totalResults, setTotalResults] = useState(0);

	const debouncedSearch = useDebouncedValue(busqueda, 350);

	const cargarUsuarios = useCallback(async () => {
		try {
			setLoading(true);

			const response = await getUsuarios({
				page,
				limit,
				search: debouncedSearch,
				rol: filtroRol,
				departamento: filtroDepartamento,
			});

			if (response?.success !== true) {
				navigate("/");
				return;
			}

			const data = response?.data || {};
			const pagination = data?.pagination || {};

			setUsuarios(Array.isArray(data?.usuarios) ? data.usuarios : []);
			setRoles(Array.isArray(data?.roles) ? data.roles : []);
			setDepartamentos(Array.isArray(data?.departamentos) ? data.departamentos : []);
			setClientes(Array.isArray(data?.clientes) ? data.clientes : []);
			setDelegaciones(Array.isArray(data?.delegaciones) ? data.delegaciones : []);
			setCampanas(Array.isArray(data?.campanas) ? data.campanas : []);
			setOpciones(Array.isArray(data?.opciones) ? data.opciones : []);
			setOpcionesUsuario(Array.isArray(data?.opciones_usuario) ? data.opciones_usuario : []);
			setTotalResults(Number(pagination?.total || 0));
			setTotalPages(Math.max(1, Number(pagination?.total_pages || 1)));
			setLoaded(true);
		} catch (error) {
			console.error("Error al cargar usuarios:", error);
			navigate("/");
		} finally {
			setLoading(false);
		}
	}, [debouncedSearch, filtroDepartamento, filtroRol, limit, navigate, page]);

	useEffect(() => {
		void cargarUsuarios();
	}, [cargarUsuarios]);

	useEffect(() => {
		if (page > totalPages) {
			setPage(totalPages);
		}
	}, [page, totalPages]);

	const handleEvent = (opcionId) => {
		if (opcionId === 1) {
			setOpenCrearUsuario(true);
		}
	};

	const handleBusqueda = (event) => {
		setBusqueda(event.target.value);
		setPage(1);
	};

	const handleFiltroRol = (event) => {
		setFiltroRol(event.target.value);
		setPage(1);
	};

	const handleFiltroDepartamento = (event) => {
		setFiltroDepartamento(event.target.value);
		setPage(1);
	};

	return (
		<div className="menu-back fade-in2">
			<SubHeader titulo="Usuarios" />

			{!loaded && <CircularProgress />}

			{loaded && (
				<>
					<Grid container direction="column" size={{ xs: 12 }}>
						<OpcionesMenuFunc opciones={opciones} handleEvent={handleEvent} />
					</Grid>

					<Grid
						container
						direction="column"
						padding="1em"
						alignItems="center"
						size={{ xs: 12 }}
					>
						<Grid container size={{ xs: 11 }}>
							<Typography variant="h4" gutterBottom>
								<PersonIcon sx={{ mr: 1, verticalAlign: "middle" }} />
								Usuarios: {totalResults}
							</Typography>
						</Grid>

						<Grid container size={{ xs: 11 }} spacing={2} sx={{ mb: 2 }}>
							<Grid item size={{ xs: 12, sm: 4 }}>
								<TextField
									label="Buscar por nombre, correo o NIF..."
									variant="standard"
									size="small"
									fullWidth
									value={busqueda}
									onChange={handleBusqueda}
								/>
							</Grid>

							<Grid item size={{ xs: 12, sm: 4 }}>
								<FormControl variant="standard" fullWidth size="small">
									<InputLabel>Rol</InputLabel>
									<Select value={filtroRol} onChange={handleFiltroRol}>
										<MenuItem value="">
											<em>Todos</em>
										</MenuItem>
										{roles.map((rol) => (
											<MenuItem key={rol.id} value={String(rol.id)}>
												{rol.nombre}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item size={{ xs: 12, sm: 4 }}>
								<FormControl variant="standard" fullWidth size="small">
									<InputLabel>Departamento</InputLabel>
									<Select value={filtroDepartamento} onChange={handleFiltroDepartamento}>
										<MenuItem value="">
											<em>Todos</em>
										</MenuItem>
										{departamentos.map((departamento) => (
											<MenuItem key={departamento.id} value={String(departamento.id)}>
												{departamento.nombre}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
						</Grid>

						<Grid
							container
							size={{ xs: 11 }}
							sx={{
								px: 2,
								py: 1,
								borderBottom: "2px solid",
								borderColor: "divider",
								fontWeight: 600,
							}}
						>
							<Grid item size={{ xs: 1 }} />
							<Grid item size={{ xs: 4 }}>
								Nombre
							</Grid>
							<Grid item size={{ xs: 2.5 }} sx={{ display: { xs: "none", md: "block" } }}>
								Rol
							</Grid>
							<Grid item size={{ xs: 1.5 }} sx={{ display: { xs: "none", md: "block" } }}>
								Estado
							</Grid>
							<Grid item size={{ xs: 2 }} sx={{ display: { xs: "none", md: "block" } }}>
								Accesos
							</Grid>
							<Grid item size={{ xs: 1 }} />
						</Grid>

						<Grid container direction="column" size={{ xs: 11 }}>
							{loading && (
								<Grid sx={{ py: 2 }}>
									<CircularProgress size={18} />
								</Grid>
							)}

							{!loading && usuarios.length > 0 ? (
								usuarios.map((usuario) => (
									<UsuarioCard
										key={usuario.id}
										usuario={usuario}
										opciones={opcionesUsuario}
										cargarUsuarios={cargarUsuarios}
									roles={roles}
									departamentos={departamentos}
									clientes={clientes}
									delegaciones={delegaciones}
									campanas={campanas}
									/>
								))
							) : null}

							{!loading && usuarios.length === 0 ? (
								<Typography variant="body1" sx={{ mt: 2 }}>
									No hay usuarios disponibles
								</Typography>
							) : null}
						</Grid>

						<Grid
							container
							size={{ xs: 11 }}
							alignItems="center"
							justifyContent="space-between"
							sx={{ pt: 2 }}
							gap={2}
						>
							<FormControl variant="standard" sx={{ minWidth: 120 }}>
								<InputLabel>Filas por pagina</InputLabel>
								<Select
									value={limit}
									onChange={(event) => {
										setLimit(Number(event.target.value));
										setPage(1);
									}}
								>
									<MenuItem value={10}>10</MenuItem>
									<MenuItem value={25}>25</MenuItem>
									<MenuItem value={50}>50</MenuItem>
								</Select>
							</FormControl>

							<Typography variant="body2" color="text.secondary">
								{totalResults} resultado(s)
							</Typography>

							<Pagination
								count={totalPages}
								page={page}
								onChange={(_, nextPage) => setPage(nextPage)}
								color="primary"
								disabled={totalResults === 0}
							/>
						</Grid>
					</Grid>

					{openCrearUsuario && (
						<FormCrearUsuario
							open={openCrearUsuario}
							setOpen={setOpenCrearUsuario}
							cargarUsuarios={cargarUsuarios}
							departamentos={departamentos}
							roles={roles}
							clientes={clientes}
							delegaciones={delegaciones}
							campanas={campanas}
						/>
					)}
				</>
			)}
		</div>
	);
};
